import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useIsMounted from "@entrilia/shared/hooks/useIsMounted";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import adminApi from "../../../../api/adminApi";

interface Props {
  taskId: string;
  onCancel: () => void;
  onDeleted: () => void;
}

const deleteAssistantTask = withErrorHandling(adminApi.deleteAssistantTask);

const DeleteTaskDialog = ({ taskId, onCancel, onDeleted }: Props) => {
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isMounted = useIsMounted();

  const handleClose = () => {
    if (!deleting) {
      onCancel();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const [, error] = await deleteAssistantTask(taskId);
    if (!isMounted()) {
      return;
    }

    setDeleting(false);
    if (error) {
      logError(error, "[DeleteTaskDialog] deleteAssistantTask");
      setErrorMessage(error.message);
      return;
    }

    onDeleted();
  };

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center", textAlign: "center" }}>
            <Typography variant="h6">Delete this task?</Typography>
            <Typography color="textSecondary">
              It won&apos;t run again. Its run chats stay in your chat list.
            </Typography>
            {errorMessage && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={deleting}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteTaskDialog;
